import { DestroyRef, inject, Injectable } from '@angular/core';
import { ExercisesCollectionService } from '../../core/firestore/collections/exercises/exercises-collection.service';
import { WorkoutsCollectionService } from '../../core/firestore/collections/workouts/workouts-collection.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { StatisticsCardProps } from './components/dashboard-statistics-card/dashboard-statistics-card.models';
import { STATISTICS_CARD_PROPS_DEFAULT } from './components/dashboard-statistics-card/dashboard-statistics-card.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PublicEventLogsCollectionService
} from '../../core/firestore/collections/public-event-logs/public-event-logs-collection.service';
import { EventLogsCollectionService } from '../../core/firestore/collections/event-logs/event-logs-collection.service';
import { PublicEventLog } from '../../core/firestore/collections/public-event-logs/public-event-logs-collection.models';
import { EventLog } from '../../core/firestore/collections/event-logs/event-logs-collection.models';
import { DateRange } from '../../shared/components/date-range-picker/date-range-picker.models';

@Injectable()
export class DashboardService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _statisticsSubject = new BehaviorSubject<StatisticsCardProps[]>(
    STATISTICS_CARD_PROPS_DEFAULT,
  );
  private readonly _exercisesCollService = inject(ExercisesCollectionService);
  private readonly _workoutsCollService = inject(WorkoutsCollectionService);
  private readonly _publicEventLogsCollService = inject(PublicEventLogsCollectionService);
  private readonly _eventLogsCollService = inject(EventLogsCollectionService);

  public statistics$ = this._statisticsSubject.asObservable();
  public exercises$ = this._exercisesCollService.exercises$;
  public workouts$ = this._workoutsCollService.workouts$;

  constructor() {
    this._initStatisticsListener();
  }

  private get _statistics(): StatisticsCardProps[] {
    return this._statisticsSubject.value;
  }

  private set _statistics(statisticsCardProps: StatisticsCardProps[]) {
    this._statisticsSubject.next(statisticsCardProps);
  }

  public async fetchExercises(): Promise<void> {
    await this._exercisesCollService.getExercises();
  }

  public async fetchWorkouts(): Promise<void> {
    await this._workoutsCollService.getWorkouts();
  }

  public getPublicEventLogs(dateRange: DateRange): Promise<PublicEventLog[]> {
    return this._publicEventLogsCollService.getPublicEventLogsByDateRange(
      dateRange.startDate,
      dateRange.endDate,
    );
  }

  public getEventLogs(dateRange: DateRange): Promise<EventLog[]> {
    return this._eventLogsCollService.getEventLogsByDateRange(
      dateRange.startDate,
      dateRange.endDate,
    );
  }

  public _initStatisticsListener(): void {
    combineLatest([this.exercises$, this.workouts$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([exercises, workouts]) => {
        const exercisesCount = exercises.length;
        const workoutsCount = workouts.length;

        this._statistics = this._statistics.map((statisticCardProp, index) => {
          if (index === 0) {
            return { ...statisticCardProp, value: exercisesCount };
          }
          if (index === 1) {
            return { ...statisticCardProp, value: workoutsCount };
          }
          return statisticCardProp;
        });
      });
  }
}
